/** @jsx jsx */
import React, { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Hidden,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ICustomTheme } from 'interfaces';

import * as S from './styles';
import { DeclarationContext } from '..';
import { StepsType } from '../interfaces';
import DeclarationNavigation from '../DeclarationNavigation';

const { cssPanel, cssProductTitle, cssProductTitleDeclaration } = S;

type StepLayoutProps = {
  title: string;
  subtitle?: string;
  step: StepsType;
  children: React.ReactNode;
};

export default function StepLayout({
  title,
  subtitle,
  step,
  children,
}: StepLayoutProps): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentStepIndex } = useContext(DeclarationContext);
  const containerRef = useRef<null | HTMLElement>(null);

  const scrollTop = (): void =>
    containerRef?.current?.scrollIntoView({ behavior: 'smooth' });

  return (
    <Paper
      ref={containerRef}
      css={() => ({
        padding: isMobile ? 16 : 48,
      })}
    >
      {/* ---------------- Desktop -------------*/}
      <Hidden smDown>
        <Grid item>
          <Typography
            paragraph
            css={{
              fontWeight: theme.typography.fontWeightBold,
              color: theme.colors.paletteFromBO.main,
            }}
          >
            {t('challenges.declaration.step', {
              step: +currentStepIndex + 1,
            })}{' '}
            {title}
          </Typography>
        </Grid>
        <Grid item>
          <Typography>{subtitle}</Typography>
          <div>
            {children}
            <DeclarationNavigation step={step} scrollTop={scrollTop} />
          </div>
        </Grid>
      </Hidden>
      {/* ---------------- Mobile -------------*/}
      <Hidden mdUp>
        <Accordion css={cssPanel}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" fontSize="large" />}
          >
            <Grid css={{ width: '100%' }}>
              <Typography
                color="primary"
                align="center"
                variant="h3"
                css={cssProductTitle}
              >
                <strong>
                  {t('challenges.declaration.step', {
                    step: +currentStepIndex + 1,
                  })}
                </strong>
              </Typography>
              <Typography
                color="primary"
                align="center"
                variant="h3"
                css={cssProductTitleDeclaration}
              >
                {title}
              </Typography>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph align="center">
              {subtitle}
            </Typography>
          </AccordionDetails>
        </Accordion>
        <div>
          {children}
          <DeclarationNavigation step={step} scrollTop={scrollTop} />
        </div>
      </Hidden>
    </Paper>
  );
}
